import { SentryError } from '@ifixit/sentry';
import { log } from './logger';

export function assertNever(_x: never): never {
   throw new SentryError('Unexpected object: ' + _x);
}

export function isRecord<T>(val: unknown): val is Record<string, T> {
   return val != null && typeof val === 'object';
}

const isProduction = process.env.NODE_ENV === 'production';
const enableLogging = Boolean(process.env.NEXT_PUBLIC_LOGGING);
const prefix = 'invariant failed';

export function invariant(condition: unknown, message: string | (() => string)): asserts condition {
   if (condition) {
      return;
   }
   const sentryMsg = `${prefix}: ${typeof message === 'function' ? message() : message}`;
   throw new SentryError(sentryMsg);
}

export function isError(_x: unknown): _x is Error {
   return _x instanceof Error;
}

/**
 * Calls the async function and logs the time it takes for the returned promise
 * to resolve.
 */
export function timeAsync<T>(name: string, asyncFunction: () => Promise<T>): Promise<T> {
   const done = time(name);
   return asyncFunction().finally(done);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AsyncFn = (...args: any[]) => Promise<any>;

/**
 * Wraps an async function so that it logs the time it takes for the
 * returned promise to resolve.
 */
export function withAsyncTiming<F extends AsyncFn>(
   name: string,
   asyncFunction: F
): (...args: Parameters<F>) => Promise<Awaited<ReturnType<F>>> {
   return async (...params: Parameters<F>) => {
      const done = time(name);
      return asyncFunction(...params).finally(done);
   };
}

export function timeSync<T>(name: string, syncFunction: () => T): T {
   const done = time(name);
   const response = syncFunction();
   done();
   return response;
}

export function withTiming<ARGS extends unknown[], RETURN>(
   name: string,
   promiseFunction: (...args: ARGS) => Promise<RETURN>
) {
   return (...args: ARGS) => {
      const done = time(name);
      return promiseFunction(...args).finally(done);
   };
}

export function withSyncTiming<ARGS extends unknown[], RETURN>(
   name: string,
   syncFunction: (...args: ARGS) => RETURN
) {
   return (...args: ARGS) => {
      const done = time(name);
      const ret = syncFunction(...args);
      done();
      return ret;
   };
}

/**
 * Timing hook stored on globalThis so it survives across bundled module
 * copies (Next.js transpilePackages can create separate instances of
 * this module in instrumentation.ts vs request-handling code).
 */
const TIMING_HOOK_KEY = '__ifixit_timing_hook__';

function getTimingHook(): ((name: string, ms: number) => void) | undefined {
   return (globalThis as Record<string, unknown>)[TIMING_HOOK_KEY] as
      | ((name: string, ms: number) => void)
      | undefined;
}

/**
 * Register a callback that receives every timing measurement.
 * Used by the Next.js app to collect per-request metrics for the
 * developer toolbar without the shared helpers depending on Next.js.
 */
export function setTimingHook(hook: (name: string, ms: number) => void) {
   (globalThis as Record<string, unknown>)[TIMING_HOOK_KEY] = hook;
}

const silentTimer = (_timerName: string) => {
   const hook = getTimingHook();
   if (!hook) return () => {};
   const t = performance.now();
   return () => hook(_timerName, performance.now() - t);
};

const loggingTimer = (_timerName: string) => {
   const t = performance.now();
   return () => {
      const taken = performance.now() - t;
      log.info.timing(_timerName, taken);
      getTimingHook()?.(_timerName, taken);
   };
};

type Timer = (name: string) => () => void;
const time: Timer = !isProduction || enableLogging ? loggingTimer : silentTimer;

export function isBlank(value: unknown): boolean {
   return value == null || isBlankString(value) || isBlankArray(value) || isBlankObject(value);
}

function isBlankString(value: unknown): boolean {
   return typeof value === 'string' && value.trim() === '';
}

function isBlankArray(value: unknown): boolean {
   return Array.isArray(value) && value.length === 0;
}

function isBlankObject(value: unknown): boolean {
   return typeof value === 'object' && value != null && Object.keys(value).length === 0;
}

/**
 * Checks if a value is present (not null, undefined, empty, or blank).
 *
 * This function considers a value as "not present" if it is:
 * - `null` or `undefined`
 * - `false`
 * - An empty string or a string with only whitespace
 * - An empty array
 * - An empty plain object (no own properties)
 *
 * @template T - The type of the value being checked
 * @param {T | null | undefined} value - The value to check for presence
 * @returns {value is NonNullable<T>} - `true` if the value is present, `false` otherwise
 *
 * @example
 * // Null and undefined values
 * isPresent(null)          // false
 * isPresent(undefined)     // false
 *
 * @example
 * // Boolean values
 * isPresent(false)         // false
 * isPresent(true)          // true
 *
 * @example
 * // String values
 * isPresent("")            // false
 * isPresent("   ")         // false
 * isPresent("hello")       // true
 * isPresent(" hello ")     // true
 *
 * @example
 * // Numeric values
 * isPresent(0)             // true
 * isPresent(-1)            // true
 * isPresent(42)            // true
 * isPresent(NaN)           // true
 *
 * @example
 * // Array values
 * isPresent([])            // false
 * isPresent([1, 2, 3])     // true
 * isPresent([null])        // true (array has elements)
 *
 * @example
 * // Object values
 * isPresent({})            // false
 * isPresent({ a: 1 })      // true
 * isPresent(new Date())    // true (not a plain object)
 *
 * @example
 * // Type narrowing in TypeScript
 * function processUser(user: User | null) {
 *   if (isPresent(user)) {
 *     // TypeScript knows 'user' is User here, not null
 *     console.log(user.name);
 *   }
 * }
 */
export function isPresent<T>(value: T | null | undefined): value is NonNullable<T> {
   if (value === null || value === undefined) return false;

   if (value === false) return false;

   if (typeof value === 'string') return value.trim().length > 0;

   if (Array.isArray(value)) return value.length > 0;

   if (typeof value === 'object' && value.constructor === Object)
      return Object.keys(value).length > 0;

   // Everything else is present (numbers including 0, true, dates, custom objects, etc.)
   return true;
}

export function presence(text: string | null | undefined): string | null {
   return isPresent(text) ? text : null;
}

export function filterNullableItems<I>(items?: (I | null | undefined)[] | null): NonNullable<I>[] {
   return (items?.filter(item => item != null) as NonNullable<I>[]) || [];
}

export function isKeyOf<T extends object>(key: PropertyKey, obj: T): key is keyof T {
   return key in obj;
}

/**
 * Executes a callback function when the browser is idle, with a fallback to setTimeout
 * for browsers that don't support requestIdleCallback.
 *
 * @param callback - Function to execute when browser is idle
 * @param options - Optional configuration object
 * @param options.timeout - Maximum time (in ms) to wait before the callback is invoked (default: 5000)
 */
export const executeWhenIdle = (callback: () => void, options: { timeout?: number } = {}): void => {
   const timeout = options.timeout ?? 5000;

   if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout });
   } else {
      // If requestIdleCallback is not available postpones execution to the next event loop cycle
      setTimeout(callback, 0);
   }
};

/**
 * Executes a callback function in the next event loop cycle, allowing the browser
 * to process any pending renders or updates before executing the callback.
 *
 * @param callback - Function to execute in the next event loop cycle
 */
export const executeNextLoop = (callback: () => void): void => {
   setTimeout(callback, 0);
};
